import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import pa from '../ProductActivityPage.module.css'
import lc from './List.module.css'
import styles from './StockSettings.module.css'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { useHelpPref } from '../../lib/preferences'
import { PendingButton } from '../../components/pending'
import { PageHeader, PageBody, TabBar, headerStyles } from '../../components/PageHeader'
import { CustomersTab } from './CustomersTab'
import { EstablishmentTab } from './EstablishmentTab'

// Configuration → Stock & Dispatch (V4 EX-03, "Stock & Dispatch settings").
// Customers and their destinations (§7n.7, CustomersTab.tsx); the free lists
// of §7n.13, storage locations, carriers & forwarders and
// brands & lids, each Active, Archived or Removed; and Box types & weights,
// which only points at General settings → Packing where they are kept.
// Every rule is the API's (routes/option-lists.js): this screen shows what it
// returns and says what it refused.
//
// Establishment (§7n.8) is a single record, not a list, so it has its own
// view and form (EstablishmentTab.tsx) rather than the list builder.
//
// Not built yet: Label templates, and V4's Prototype data tab, which has no
// place in a real workspace.

export type OptionKind = 'locations' | 'carriers' | 'lids'
type TabKey = 'customers' | OptionKind | 'boxes' | 'establishment'

const TABS: [TabKey, string][] = [
  ['customers', 'Customers'],
  ['locations', 'Storage locations'],
  ['carriers', 'Carriers & forwarders'],
  ['lids', 'Brands & lids'],
  ['boxes', 'Box types & weights'],
  ['establishment', 'Establishment'],
]

export interface OptionItem {
  id: string
  name: string
  archived: boolean
  archived_at: string | null
  in_use: string[]
  state: 'archived' | 'in_use' | 'active'
  removable: boolean
}

// V4 SE_HELP, verbatim, except Customers: its first card drops "incoterm and
// receiving establishment" (neither is on the destination here: CUST-3) and
// its Xero card goes with the pricing permission (#22).
const HELP: Record<TabKey, { title: string; parts: [string, string][] }> = {
  customers: {
    title: 'What a customer record governs',
    parts: [
      ['Destinations carry the detail', 'A customer can have several destinations, and the address, port and consignee live on the destination, not the customer. An order names one of them, so a destination that is wrong here is wrong on every docket.'],
      ['Label rules are matched from here', 'Customer, destination and transport mode decide which label template applies. Where two match or none does, printing asks for an explicit choice and records it.'],
      ['Archive rather than delete', 'Archiving stops new use and keeps every historical order readable. Deleting is only possible while nothing uses the record.'],
    ],
  },
  locations: {
    title: 'What storage locations are for',
    parts: [
      ['Named places, not a rack map', 'A pallet is somewhere, Coolstore B, the loading dock, not at a coordinate. The list exists so people can find stock, not to model the building.'],
      ['Used on the pallet, not the order', 'Location is pallet data and moves with the Move action, which records who moved it and when.'],
      ['Archive rather than delete', 'A location in use cannot be removed: pallets keep the value they were saved with, so deleting would leave them pointing at nothing.'],
    ],
  },
  carriers: {
    title: 'What carriers and forwarders govern',
    parts: [
      ['Selectable on a load', 'The carrier is the company moving the truck. It belongs to the load, alongside vehicle and booking reference.'],
      ['Freight references belong to the order', 'Airway bill, flight, container and forwarder are order data, so a shared truck never carries one customer’s paperwork onto another’s.'],
      ['Archive rather than delete', 'Past loads keep the carrier they departed with; archiving retires it from new loads without rewriting history.'],
    ],
  },
  lids: {
    title: 'What brands and lids govern',
    parts: [
      ['Two places, one list', 'The lid is chosen at picking (what went on the box) and required on an order line (what the customer expects). Both read from this list.'],
      ['A mismatch blocks loading', 'Where a pallet’s applied lid differs from the lid its order line requires, the pallet cannot be loaded. The wrong lid is a customer rejection, not a paperwork detail.'],
      ['Archive rather than delete', 'A retired brand stays readable on the jobs and orders that used it.'],
    ],
  },
  boxes: {
    title: 'How box types drive everything downstream',
    parts: [
      ['Boxes per pallet is a snapshot', 'Capacity is copied onto a picking job when the job is saved and never recalculated afterwards. Changing it here affects new jobs only. Existing jobs and pallets keep the figure they were created with.'],
      ['Net weight and tolerances', 'Net weight is what the box should hold; min and max are the acceptable range PackRight weighs against. Too narrow and the floor fights the scale; too wide and short boxes reach the customer.'],
      ['Box type separates stock', 'Two otherwise identical picks in different box types are different source groups and cannot share a pallet.'],
    ],
  },
  establishment: {
    title: 'What this is used for',
    parts: [
      ['Your identity on export paperwork', 'The establishment name and registration number print as the sender on export labels and documents. One per subscription.'],
      ['Not the same as a destination', 'The receiving establishment belongs to the customer’s destination; this is yours.'],
    ],
  },
}

// V4 seListNote / seInUseNote, verbatim.
const LIST_NOTE: Record<OptionKind, string> = {
  locations: 'Simple named locations. No rack map. A pallet is somewhere, not at a coordinate.',
  carriers: 'Selectable on a load. Airway bills and containers belong to the order, not the carrier.',
  lids: 'Lid/brand options offered at picking and on order lines.',
}
const IN_USE_NOTE =
  'Archiving is the normal way to retire an option: it stays readable on every record that already carries it and is no longer offered on new ones. Removing outright is only possible while nothing uses it and it is not archived. Records keep the value saved at the time, so removing something in use would leave them pointing at nothing.'

// The server's comparison: trimmed, inner spaces collapsed, ignoring case.
export const sameName = (a: string, b: string) => a.trim().replace(/\s+/g, ' ').toLowerCase() === b.trim().replace(/\s+/g, ' ').toLowerCase()

const errText = (e: any) => e?.body?.error || e?.message || String(e)

export function StockSettingsPage() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const asked = params.get('tab') as TabKey | null
  const tab: TabKey = TABS.some(([k]) => k === asked) ? asked! : 'customers'
  // ?customer=<id> opens that customer's drawer over the list.
  const openCustomer = tab === 'customers' ? params.get('customer') : null
  const setOpenCustomer = (id: string | null) => setParams(id ? { tab: 'customers', customer: id } : { tab: 'customers' }, { replace: true })
  // One preference per tab (§7n.19: a list screen with tabs keeps one per
  // tab), so hiding the Customers help does not hide the Carriers help.
  const [helpOn, toggleHelp] = useHelpPref(user, `stock_settings_${tab}`)
  const help = HELP[tab]

  const tabs = (
    <TabBar label="Stock & Dispatch settings">
      {TABS.map(([k, label]) => (
        <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? headerStyles.tabOn : headerStyles.tab} onClick={() => setParams({ tab: k }, { replace: true })}>
          {label}
        </button>
      ))}
      <button type="button" className={`${lc.helpPill} ${styles.tabHelp}`} onClick={toggleHelp} aria-expanded={helpOn}>
        {helpOn ? 'Hide help' : 'How this tab works'}
      </button>
    </TabBar>
  )

  return (
    <div className={styles.container}>
      <PageHeader
        title="Stock & Dispatch settings"
        tabs={tabs}
      />

      <PageBody>
        {helpOn && (
          <div className={`${lc.help} ${styles.help}`} data-testid="stock-settings-help">
            <div className={lc.helpHead}>
              <span className={lc.helpTitle}>{help.title}</span>
              <button type="button" className={lc.helpX} title="Hide help" aria-label="Hide help" onClick={toggleHelp}>
                ×
              </button>
            </div>
            {help.parts.map(([k, v]) => (
              <div key={k} className={lc.helpItem}>
                <span>{k}</span>
                <p>{v}</p>
              </div>
            ))}
          </div>
        )}
        {tab === 'customers' ? (
          <CustomersTab user={user} openId={openCustomer} onOpen={setOpenCustomer} />
        ) : tab === 'boxes' ? (
          <BoxesElsewhere />
        ) : tab === 'establishment' ? (
          <EstablishmentTab user={user} />
        ) : (
          <OptionList key={tab} kind={tab} />
        )}
      </PageBody>
    </div>
  )
}

function BoxesElsewhere() {
  const navigate = useNavigate()
  return (
    <div className={styles.elsewhere}>
      <h2>Box types &amp; weights live in General settings</h2>
      <p>
        Box types carry boxes per pallet and the weight window PackRight weighs against, so they belong to your product rather than this module. They
        are maintained in one place, General settings → Packing, and Stock &amp; Dispatch reads them.
      </p>
      <button type="button" className={styles.elsewhereBtn} onClick={() => navigate('/settings?tab=packing')}>
        Open General settings → Packing
      </button>
    </div>
  )
}

function OptionList({ kind }: { kind: OptionKind }) {
  const { user } = useAuth()
  const [items, setItems] = useState<OptionItem[] | null>(null)
  const [loadError, setLoadError] = useState('')
  const [error, setError] = useState('')
  const [draft, setDraft] = useState('')
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const busy = busyOn !== null
  const [toast, setToast] = useState('')
  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const base = `/api/settings/options/${kind}`

  const load = useCallback(async () => {
    if (!userRef.current) return
    try {
      setItems((await apiJson<{ items: OptionItem[] }>(userRef.current, base)).items)
      setLoadError('')
    } catch (e) {
      setLoadError(`Failed to load the list: ${errText(e)}`)
    }
  }, [base])

  useEffect(() => {
    if (uid) load()
  }, [uid, load])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  // One change at a time; the list is re-read afterwards so every row's
  // "In use" and remove control are the server's again.
  const act = async (key: string, path: string, method: 'POST' | 'DELETE', body: unknown, done: (r: any) => string) => {
    setBusyOn(key)
    setError('')
    try {
      const r = await apiJson<any>(userRef.current, path, body === undefined ? { method } : { method, body })
      setToast(done(r))
      await load()
      return true
    } catch (e) {
      setError(errText(e))
      await load()
      return false
    } finally {
      setBusyOn(null)
    }
  }

  const dup = draft.trim() && (items || []).some((i) => sameName(i.name, draft)) ? 'Already on the list.' : ''

  const add = async () => {
    const v = draft.trim()
    if (!v || dup || busy) return
    if (await act('add', base, 'POST', { name: v }, (r) => `Added ${r.name}`)) setDraft('')
  }

  const archive = (i: OptionItem) => act(`archive:${i.id}`, `${base}/${i.id}/archive`, 'POST', undefined, () => `${i.name} archived. Stays on existing records, not offered on new ones`)
  const restore = (i: OptionItem) => act(`restore:${i.id}`, `${base}/${i.id}/restore`, 'POST', undefined, () => `${i.name} restored: offered again on new records`)
  const remove = (i: OptionItem) => act(`remove:${i.id}`, `${base}/${i.id}`, 'DELETE', undefined, () => `Removed ${i.name}`)

  return (
    <>
      <div className={styles.note}>{LIST_NOTE[kind]}</div>
      <div className={styles.subNote}>{IN_USE_NOTE}</div>
      {loadError && (
        <div className={styles.error} role="alert">
          {loadError}
        </div>
      )}
      <div className={styles.listCard}>
        {items && items.length === 0 && <div className={styles.emptyRow}>Nothing on this list yet.</div>}
        {!items && !loadError && <div className={styles.emptyRow}>Loading…</div>}
        {(items || []).map((i) => (
          <div key={i.id} className={styles.row} data-testid="option-row">
            <span className={i.archived ? styles.nameArchived : styles.name}>
              {i.name}
              {i.in_use.length > 0 && <span className={styles.use}>In use · {i.in_use.join(' · ')}</span>}
            </span>
            {i.state === 'archived' && <span className={styles.chipArchived}>Archived</span>}
            {i.state === 'in_use' && <span className={styles.chip}>In use</span>}
            {i.archived ? (
              <PendingButton type="button" className={styles.rowBtn} busy={busyOn === `restore:${i.id}`} disabled={busy || (busyOn !== null && busyOn !== `restore:${i.id}`)} pending="Restoring…" onClick={() => restore(i)}>
                Restore
              </PendingButton>
            ) : (
              <PendingButton type="button" className={styles.rowBtn} busy={busyOn === `archive:${i.id}`} disabled={busy || (busyOn !== null && busyOn !== `archive:${i.id}`)} title="Stop offering this on new records; existing records keep it" pending="Archiving…" onClick={() => archive(i)}>
                Archive
              </PendingButton>
            )}
            {i.removable && (
              <PendingButton type="button" className={styles.removeBtn} busy={busyOn === `remove:${i.id}`} disabled={busy || (busyOn !== null && busyOn !== `remove:${i.id}`)} title="Remove from the list: nothing uses it" aria-label={`Remove ${i.name}`} pending="Removing…" onClick={() => remove(i)}>
                ×
              </PendingButton>
            )}
          </div>
        ))}
        {items && (
          <div className={styles.addRow}>
            <input
              className={styles.addInput}
              placeholder="Add…"
              aria-label="Add to the list"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add()
              }}
            />
            <PendingButton type="button" className={styles.addBtn} busy={busyOn === 'add'} disabled={busy || (busyOn !== null && busyOn !== 'add')} pending="Adding…" onClick={add}>
              Add
            </PendingButton>
            {dup && <span className={styles.addErr}>{dup}</span>}
          </div>
        )}
      </div>
      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </>
  )
}
